import React, { useEffect, useState } from 'react'
import { Activity, CheckCircle, XCircle, Loader } from 'lucide-react'

const BASE = import.meta.env.VITE_API_BASE_URL || ''

export default function OAuthCallback() {
  const [status,  setStatus]  = useState('loading')  // 'loading' | 'success' | 'error'
  const [message, setMessage] = useState('')

  useEffect(() => {
    const params  = new URLSearchParams(window.location.search)
    const code    = params.get('code')
    const session = params.get('session')
    const error   = params.get('error')

    if (error) {
      setStatus('error')
      setMessage(`Schwab authorization denied: ${error}`)
      return
    }
    if (!code) {
      setStatus('error')
      setMessage('No authorization code received from Schwab.')
      return
    }

    // Exchange the code for tokens.
    // This endpoint intentionally has no auth requirement — the Schwab code
    // is single-use and expires in ~30 seconds, providing its own security.
    // We also try the Bearer token from localStorage in case it's available.
    const token = localStorage.getItem('sf_auth_token')

    fetch(`${BASE}/api/schwab/exchange-code`, {
      method:  'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ code, session }),
    })
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          setStatus('success')
          setMessage('Schwab connected successfully.')
          // Notify the opener (Settings page) so it refreshes its status badge
          try {
            if (window.opener && !window.opener.closed) {
              window.opener.postMessage({ type: 'SCHWAB_AUTH_SUCCESS' }, window.location.origin)
            }
          } catch (_) { /* noopener context — opener is not accessible, that's fine */ }
        } else {
          setStatus('error')
          setMessage(data.error || 'Token exchange failed — check backend logs.')
        }
      })
      .catch(e => {
        setStatus('error')
        setMessage(`Network error: ${e.message}`)
      })
  }, [])

  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{ background: 'linear-gradient(135deg, #060d1a 0%, #0d1321 60%, #0a1628 100%)' }}
    >
      <div className="text-center max-w-sm px-6">

        {/* Logo */}
        <div className="flex justify-center mb-6">
          <div
            className="flex items-center justify-center w-10 h-10 rounded-xl"
            style={{ background: 'rgba(34,211,238,0.08)', border: '1px solid rgba(34,211,238,0.2)' }}
          >
            <Activity className="h-5 w-5 text-cyan-400" />
          </div>
        </div>

        {status === 'loading' && (
          <>
            <Loader className="h-8 w-8 text-cyan-400 animate-spin mx-auto mb-4" />
            <p className="text-slate-300 font-medium mb-2"
              style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.85rem' }}>
              Connecting to Schwab...
            </p>
            <p className="text-slate-600"
              style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.65rem' }}>
              Exchanging authorization code for tokens
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="h-10 w-10 text-emerald-400 mx-auto mb-4" />
            <p className="text-emerald-300 font-medium mb-2"
              style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.85rem' }}>
              {message}
            </p>
            <p className="text-slate-600 mt-4"
              style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.65rem' }}>
              You can close this tab and return to the dashboard.
            </p>
            <button
              onClick={() => window.close()}
              className="mt-6 px-5 py-2 rounded-lg text-xs font-medium"
              style={{
                background: 'rgba(16,185,129,0.1)', color: '#10b981',
                border: '1px solid rgba(16,185,129,0.3)',
                fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.08em',
                cursor: 'pointer',
              }}
            >
              CLOSE TAB
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="h-10 w-10 text-rose-400 mx-auto mb-4" />
            <p className="text-rose-300 font-medium mb-2"
              style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.85rem' }}>
              Authorization Failed
            </p>
            <p className="text-slate-500 mt-2"
              style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.65rem' }}>
              {message}
            </p>
            <p className="text-slate-700 mt-4"
              style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.62rem' }}>
              Close this tab and try again from the Settings page.
            </p>
          </>
        )}
      </div>
    </div>
  )
}
